import { Button as AntButton, message, Space, Tooltip } from "antd";
import classNames from "classnames";
import { Button } from "react-bootstrap";
import { AiOutlineCalendar } from "react-icons/ai";

import { DatePicker, FontFa } from "../../../../../components";
import { DateHelper, StringHelper } from "../../../../../helper";

import * as docx from "docx";
import { saveAs } from "file-saver";
import { FileDown } from "lucide-react";
import { RequestStatus } from "../../../../../constants";
import { AFLType } from "../models";
import { DocumentAFLCreator } from "../services";
import { getWordValue } from "../utils";
import { useAFLSteps } from "./use-AFL-steps";
import useDownloadLeaveRequestTemplate from "./use-download-leave-request-template";
import { useSearchColumn } from "./use-search-column";

const getApprovalProcess = (data: any) => {
  if (data.eRequestStatus !== RequestStatus.submitted) return "";

  const steps = data.eSteps || ([] as any);

  const currentStepIndex = steps.findIndex((step: any) =>
    step.some((item: any) => !item.isRejected && !item.isApproved),
  );
  const currentStep = steps[currentStepIndex];

  if (!currentStep || !currentStep.length) return "";

  const mainApproval = currentStep.find((item: any) => item.dutyType === "MAIN") || currentStep[0];

  return `${currentStepIndex}/${steps.length} chờ ${mainApproval.name} duyệt`;
};

const getWordTitle = (type: AFLType) => {
  switch (type) {
    case AFLType.AFLNoSalary:
      return "ĐƠN XIN NGHỈ KHÔNG LƯƠNG";
    case AFLType.AFLSuspendContract:
      return "ĐƠN XIN TẠM HOÃN HỢP ĐỒNG LAO ĐỘNG";
    case AFLType.AccidentLeaveApplication:
      return "ĐƠN XIN NGHỈ TAI NẠN RỦI RO /TAI NẠN GIAO THÔNG/ĐIỀU TRỊ BỆNH NGHỀ NGHIỆP/BỆNH DÀI NGÀY";
    case AFLType.AccidentNotification:
      return "THÔNG BÁO TAI NẠN RỦI RO/TAI NẠN GIAO THÔNG";
    case AFLType.ApplicationForLeave:
      return "ĐƠN XIN NGHỈ PHÉP";
    case AFLType.MaternityLeaveApplication:
      return "THÔNG BÁO V/V NGHỈ THAI SẢN";
    case AFLType.SickLeaveApplication:
      return "ĐƠN XIN NGHỈ ỐM/NUÔI CON ỐM";
    case AFLType.TerminationContractApplication:
      return "ĐƠN XIN CHẤM DỨT HỢP ĐỒNG LAO ĐỘNG";
    default:
      return "";
  }
};

const { RangePicker } = DatePicker;

const getVacationPeriodsFilterProperties = () => {
  return {
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => {
      const rangeDateValue: any = selectedKeys[0]
        ? DateHelper.rangeDateStringifyToMoment(selectedKeys[0].toString())
        : [];
      return (
        <div
          style={{
            padding: 8,
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <RangePicker
            value={rangeDateValue}
            onChange={(dates: any) => setSelectedKeys(dates ? [JSON.stringify(dates)] : [])}
          />
          <Space style={{ display: "flex", justifyContent: "space-between" }}>
            <AntButton
              onClick={() => {
                if (clearFilters) clearFilters();
                confirm();
              }}
              type="text"
              size="small"
            >
              Reset
            </AntButton>
            <AntButton onClick={() => confirm()} type="primary" size="small">
              Ok
            </AntButton>
          </Space>
        </div>
      );
    },
    filterIcon: (filtered: boolean) => <AiOutlineCalendar size={18} color={filtered ? "#007aff" : "#333"} />,
    onFilter: (s: string | number | boolean, record: any) => {
      const rangeDate = DateHelper.rangeDateStringifyToMoment(s as string);
      if (!rangeDate || !rangeDate.length) return true;

      const eVacationPeriods = record.eVacationPeriods;
      if (!eVacationPeriods || !Array.isArray(eVacationPeriods)) return false;

      const filterStart = rangeDate[0].clone().startOf("day");
      const filterEnd = rangeDate[1].clone().endOf("day");

      // Kiểm tra xem có bất kỳ range nào trong eVacationPeriods overlap với filter range không
      return eVacationPeriods.some((range: any) => {
        if (!range || !Array.isArray(range) || range.length < 2) return false;
        const rangeStart = range[0].clone().startOf("day");
        const rangeEnd = range[1].clone().endOf("day");

        // Overlap nếu: rangeStart <= filterEnd && rangeEnd >= filterStart
        return rangeStart.isSameOrBefore(filterEnd) && rangeEnd.isSameOrAfter(filterStart);
      });
    },
  };
};

export const useAFLColumns = () => {
  const { process } = useAFLSteps();
  const { getSearchTextColumnProperties, getSearchCalendarColumnProperties } = useSearchColumn();

  const { mutateAsync: downloadLeaveRequestTemplate, isLoading: isDownloadingLeaveRequestTemplate } =
    useDownloadLeaveRequestTemplate();

  const loadingKey = "export-leave-request-template";

  const handleExportFile = (id: any) => {
    message.loading({ content: "Đang xuất tài liệu...", key: loadingKey, duration: 0 });

    downloadLeaveRequestTemplate(
      {
        id: Number(id),
      },
      {
        onSuccess: () => {
          message.success({ content: "Xuất tài liệu thành công", key: loadingKey });
        },
        onError: () => {
          message.error({ content: "Xuất tài liệu thất bại", key: loadingKey });
        },
        onSettled: () => {
          message.destroy(loadingKey);
        },
      },
    );
  };

  const columns: any[] = [
    {
      title: "Xuất tài liệu",
      dataIndex: "export",
      key: "export",
      align: "center" as const,
      width: 120,
      fixed: true,
      render: (text: string, record: any) => (
        <Tooltip title="Xuất file">
          <AntButton
            danger
            loading={isDownloadingLeaveRequestTemplate}
            disabled={record.eRequestStatus !== RequestStatus.completed}
            icon={<FileDown size={16} />}
            onClick={(e: any) => {
              e.stopPropagation();

              handleExportFile(record.id);
            }}
          />
        </Tooltip>
      ),
    },
    {
      title: "Mã yêu cầu",
      dataIndex: "id",
      key: "id",
      align: "center" as const,
      width: 140,
      fixed: true,
      ...getSearchTextColumnProperties({
        inputSearchProps: { placeholder: "Tìm kiếm" },
        dataIndex: "id",
      }),
    },
    {
      title: "Ngày tạo ",
      dataIndex: "createdDateTime",
      key: "createdDateTime",
      render: (text: string) => <span>{DateHelper.formatDateToString(text)}</span>,
      ...getSearchCalendarColumnProperties({
        dataIndex: "createdDateTime",
        inputSearchProps: { placeholder: "Tìm kiếm" },
      }),
    },
    {
      title: "Người yêu cầu",
      dataIndex: "eRequestName",
      key: "eRequestName",
      render: (text: string, record: any) => (
        <span style={{ textTransform: "capitalize" }}>
          {record.eEmployeeId} - {text}
        </span>
      ),
      ...getSearchTextColumnProperties({
        inputSearchProps: { placeholder: "Tìm kiếm" },
        dataIndex: "eRequestName",
      }),
    },
    {
      title: "Thời gian nghỉ",
      dataIndex: "eVacationPeriods",
      key: "eVacationPeriods",
      align: "center" as const,
      width: 210,
      render: (eVacationPeriods: any) => {
        if (!eVacationPeriods || !Array.isArray(eVacationPeriods)) return "-";
        return (
          <span>{eVacationPeriods.map((range: any) => DateHelper.formatRangeDateToString(range)).join(", ")}</span>
        );
      },
      ...getVacationPeriodsFilterProperties(),
    },
    {
      title: "Số ngày nghỉ",
      dataIndex: "eVacationPeriods",
      key: "eVacationPeriods",
      align: "center" as const,
      render: (_: any, data: any) => {
        if (!data.eTotalLeaveDay) return "-";

        return data.eTotalLeaveDay
          ? `${data.eTotalLeaveDay} ngày`
          : DateHelper.duration(data?.eVacationPeriods).humanize();
      },
      ...getVacationPeriodsFilterProperties(),
    },
    {
      title: "Đội/Nhóm",
      key: "eTeam",
      dataIndex: "eTeam",
      render: (text: string) => <span>{StringHelper.verticalBarSplit(text)[1]}</span>,
      ...getSearchTextColumnProperties({
        inputSearchProps: { placeholder: "Tìm kiếm" },
        dataIndex: "eTeam",
      }),
    },
    {
      title: "Đơn vị",
      key: "eUnit",
      dataIndex: "eUnit",
      render: (text: string) => <span>{StringHelper.verticalBarSplit(text)[1]}</span>,
      ...getSearchTextColumnProperties({
        inputSearchProps: { placeholder: "Tìm kiếm" },
        dataIndex: "eUnit",
      }),
    },
    {
      title: "Cơ quan/Đơn vị",
      key: "eDepartment",
      dataIndex: "eDepartment",
      render: (text: string) => <span>{StringHelper.verticalBarSplit(text)[1]}</span>,
      ...getSearchTextColumnProperties({
        inputSearchProps: { placeholder: "Tìm kiếm" },
        dataIndex: "eDepartment",
      }),
    },
    {
      title: "Trạng thái",
      dataIndex: "eRequestStatus",
      key: "eRequestStatus",
      render: (_: string, record: any) => {
        const { status } = process({
          steps: record?.eSteps,
          current: record.eApprovalProcess.current,
          eRequestStatus: record.eRequestStatus,
        });
        const approvalName = getApprovalProcess(record);

        return (
          <div>
            {status.label === "Đã duyệt" || <div className={classNames("flag", status.flag)}>{status.label}</div>}
            {approvalName && <span className="text-xs text-gray-500">{approvalName}</span>}
          </div>
        );
      },
    },
    {
      title: "Lý do",
      key: "eReason",
      dataIndex: "eReason",

      render: (text: string) => (
        <div
          style={{
            maxWidth: "500px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            lineHeight: "1.4em",
            maxHeight: "4.2em", // 3 lines * 1.4em line-height
          }}
          title={text}
        >
          {text}
        </div>
      ),
      ...getSearchTextColumnProperties({
        inputSearchProps: { placeholder: "Tìm kiếm" },
        dataIndex: "eReason",
      }),
    },
  ];
  const columns2: any[] = [
    {
      title: "Xuất tài liệu",
      dataIndex: "export",
      key: "export",
      align: "center" as const,
      width: 120,
      fixed: true,
      render: (text: string, record: any) => (
        <Button
          title={"Xuất file"}
          variant="outline-danger"
          onClick={(e: any) => {
            e.stopPropagation();
            const documentAFLCreator = new DocumentAFLCreator();
            const value = getWordValue(record);

            const doc = documentAFLCreator.create(value);

            const wordTitle = `${getWordTitle(value.aflType)} - ${DateHelper.getNow()}.docx`;

            docx.Packer.toBlob(doc).then((blob) => {
              saveAs(blob, wordTitle);
            });
          }}
        >
          <FontFa name="faPrint" size={"xs"} />
        </Button>
      ),
    },
    {
      title: "Mã yêu cầu",
      dataIndex: "id",
      key: "id",
      align: "center" as const,
      width: 140,
      fixed: true,
      ...getSearchTextColumnProperties({
        inputSearchProps: { placeholder: "Tìm kiếm" },
        dataIndex: "id",
      }),
    },
    {
      title: "Ngày tạo ",
      dataIndex: "createdDateTime",
      key: "createdDateTime",
      render: (text: string) => <span>{DateHelper.formatDateToString(text)}</span>,
      ...getSearchCalendarColumnProperties({
        dataIndex: "createdDateTime",
        inputSearchProps: { placeholder: "Tìm kiếm" },
      }),
    },
    {
      title: "Người yêu cầu",
      dataIndex: "eRequestName",
      key: "eRequestName",
      render: (text: string, record: any) => (
        <span style={{ textTransform: "capitalize" }}>
          {record.eEmployeeId} - {text}
        </span>
      ),
      ...getSearchTextColumnProperties({
        inputSearchProps: { placeholder: "Tìm kiếm" },
        dataIndex: "eRequestName",
      }),
    },
    {
      title: "Thời gian nghỉ",
      dataIndex: "eVacationPeriods",
      key: "eVacationPeriods",
      align: "center" as const,
      width: 210,
      render: (eVacationPeriods: any) => {
        if (!eVacationPeriods || !Array.isArray(eVacationPeriods)) return "-";
        return (
          <span>{eVacationPeriods.map((range: any) => DateHelper.formatRangeDateToString(range)).join(", ")}</span>
        );
      },
      ...getVacationPeriodsFilterProperties(),
    },
    {
      title: "Số ngày nghỉ",
      dataIndex: "eVacationPeriods",
      key: "eVacationPeriods",
      align: "center" as const,
      render: (_: any, data: any) => {
        if (!data.eTotalLeaveDay) return "-";

        return data.eTotalLeaveDay
          ? `${data.eTotalLeaveDay} ngày`
          : DateHelper.duration(data?.eVacationPeriods).humanize();
      },
      ...getVacationPeriodsFilterProperties(),
    },
    {
      title: "Đội/Nhóm",
      key: "eTeam",
      dataIndex: "eTeam",
      render: (text: string) => <span>{StringHelper.verticalBarSplit(text)[1]}</span>,
      ...getSearchTextColumnProperties({
        inputSearchProps: { placeholder: "Tìm kiếm" },
        dataIndex: "eTeam",
      }),
    },
    {
      title: "Đơn vị",
      key: "eUnit",
      dataIndex: "eUnit",
      render: (text: string) => <span>{StringHelper.verticalBarSplit(text)[1]}</span>,
      ...getSearchTextColumnProperties({
        inputSearchProps: { placeholder: "Tìm kiếm" },
        dataIndex: "eUnit",
      }),
    },
    {
      title: "Cơ quan/Đơn vị",
      key: "eDepartment",
      dataIndex: "eDepartment",
      render: (text: string) => <span>{StringHelper.verticalBarSplit(text)[1]}</span>,
      ...getSearchTextColumnProperties({
        inputSearchProps: { placeholder: "Tìm kiếm" },
        dataIndex: "eDepartment",
      }),
    },
    {
      title: "Trạng thái",
      dataIndex: "eRequestStatus",
      key: "eRequestStatus",
      render: (_: string, record: any) => {
        const { status } = process({
          steps: record?.eSteps,
          current: record.eApprovalProcess.current,
          eRequestStatus: record.eRequestStatus,
        });
        const approvalName = getApprovalProcess(record);

        return (
          <div>
            <div className={classNames("flag", status.flag)}>{status.label}</div>
            {approvalName && <span className="text-xs text-gray-500">{approvalName}</span>}
          </div>
        );
      },
    },
    {
      title: "Lý do",
      key: "eReason",
      dataIndex: "eReason",
      render: (text: string) => (
        <div
          style={{
            maxWidth: "500px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            lineHeight: "1.4em",
            maxHeight: "4.2em", // 3 lines * 1.4em line-height
          }}
          title={text}
        >
          {text}
        </div>
      ),
      ...getSearchTextColumnProperties({
        inputSearchProps: { placeholder: "Tìm kiếm" },
        dataIndex: "eReason",
      }),
    },
  ];

  return { columns, columns2 };
};
